import React, { useState, useEffect } from 'react';
import { Calendar, User } from 'lucide-react';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog`);
      const data = await response.json();
      setBlogPosts(data);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[300px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1534361960057-19889db9621e"
            alt="Blog"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Blog & Sağlık İpuçları
          </h1>
          <p className="text-xl text-emerald-50">
            Evcil dostlarınızın sağlığı için faydalı bilgiler ve öneriler
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {post.date}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900 mb-3 group-hover:text-emerald-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/blog/${post.id}`}
                    className="text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-2"
                  >
                    Devamını Oku
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Additional Blog Content */}
          <div className="mt-20 grid lg:grid-cols-2 gap-12">
            {/* Tips Section */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h2 className="text-3xl font-bold text-emerald-900 mb-6">
                Günlük Bakım İpuçları
              </h2>
              <ul className="space-y-4">
                {[
                  {
                    title: 'Düzenli Veteriner Kontrolü',
                    text: 'Yılda en az iki kez veteriner kontrolü yaptırın'
                  },
                  {
                    title: 'Dengeli Beslenme',
                    text: 'Yaşa ve ırka uygun kaliteli mama seçin'
                  },
                  {
                    title: 'Egzersiz',
                    text: 'Günlük düzenli yürüyüş ve oyun zamanı ayırın'
                  },
                  {
                    title: 'Diş Bakımı',
                    text: 'Haftada 2-3 kez diş fırçalama yapın'
                  },
                  {
                    title: 'Parazit Kontrolü',
                    text: 'Düzenli iç ve dış parazit koruması sağlayın'
                  }
                ].map((tip, index) => (
                  <li key={index} className="flex gap-4">
                    <div className="bg-emerald-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-700 font-bold">{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{tip.title}</h4>
                      <p className="text-gray-600">{tip.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* FAQ Section */}
            <div className="bg-gradient-to-br from-emerald-50 to-amber-50 p-8 rounded-2xl">
              <h2 className="text-3xl font-bold text-emerald-900 mb-6">
                Sık Sorulan Sorular
              </h2>
              <div className="space-y-6">
                {[
                  {
                    q: 'Köpeğim ne zaman aşılanmalı?',
                    a: 'İlk aşı 6-8 haftalık iken yapılır ve düzenli aşı programı takip edilmelidir.'
                  },
                  {
                    q: 'Kısırlaştırma ne zaman yapılmalı?',
                    a: '6-9 aylık yaşta yapılması önerilir. Erken kısırlaştırma bazı sağlık problemlerini önler.'
                  },
                  {
                    q: 'Kuşumun tüyleri neden dökülüyor?',
                    a: 'Mevsimsel tüy dökümü normaldir, ancak aşırı dökülme beslenme eksikliği veya stres göstergesi olabilir.'
                  },
                  {
                    q: 'Hangi besinler zehirleyicidir?',
                    a: 'Çikolata, üzüm, soğan, sarımsak ve xylitol içeren gıdalar evcil hayvanlar için tehlikelidir.'
                  }
                ].map((faq, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                    <h4 className="font-bold text-gray-900 mb-2">{faq.q}</h4>
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-800 via-emerald-700 to-amber-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Sağlık İpuçlarını Kaçırmayın
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Evcil dostlarınızın sağlığı için faydalı bilgileri e-posta ile alın
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="E-posta adresiniz"
              className="flex-1 px-6 py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <button className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
              Abone Ol
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
